var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, isNil, useClasses, useIsomorphicLayoutEffect, useLayoutProps, useProps, } from '@hawkins/helpers';
import { IconAlert } from '@hawkins/icons/lib/iconAlert';
import { IconLock } from '@hawkins/icons/lib/iconLock';
import { IconSearch } from '@hawkins/icons/lib/iconSearch';
import { IconSuccess } from '@hawkins/icons/lib/iconSuccess';
import { IconWarning } from '@hawkins/icons/lib/iconWarning';
import { useTheme } from '@hawkins/styles';
import { FormFieldContainer } from '../formFieldContainer';
import { FormGroupContext } from '../formGroup';
import { useCombineRefs, useDynamicLayoutCalculation, useId } from '../hooks';
import { useInputStyles } from './input.styles';
var displayName = 'Input';
var iconSize = 'small';
function setMultiLineInputHeight(_a) {
    var el = _a.el, rows = _a.rows, rowsMax = _a.rowsMax;
    if (typeof window === 'undefined' || !el.style)
        return;
    var lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
    var rowsMaxNum = Number(rowsMax);
    var rowsNum = Number(rows);
    el.style.height = '0';
    var paddingVertical = el.offsetHeight;
    var newInputHeight = el.scrollHeight;
    // Check for truthiness to filter out 0 and NaN.
    if (lineHeight) {
        if (rowsNum) {
            newInputHeight = Math.max(newInputHeight, rowsNum * lineHeight + paddingVertical);
        }
        if (rowsMaxNum) {
            newInputHeight = Math.min(newInputHeight, rowsMaxNum * lineHeight + paddingVertical);
        }
    }
    el.style.height = "".concat(newInputHeight, "px");
}
/**
 * An input field is a place to enter or edit text.
 */
export var Input = React.forwardRef(function (props, ref) {
    var _a, _b, _c;
    // Props
    var _d = useProps(displayName, props), _e = _d.active, active = _e === void 0 ? false : _e, ariaLabel = _d["aria-label"], autoComplete = _d.autoComplete, autoFocus = _d.autoFocus, _f = _d.autoEndAdornment, autoEndAdornment = _f === void 0 ? true : _f, _g = _d.autoStartAdornment, autoStartAdornment = _g === void 0 ? true : _g, _h = _d.borderless, borderless = _h === void 0 ? false : _h, classesProp = _d.classes, className = _d.className, cols = _d.cols, _j = _d.compact, compact = _j === void 0 ? false : _j, _k = _d.component, Component = _k === void 0 ? 'div' : _k, containerClasses = _d.containerClasses, containerProps = _d.containerProps, defaultValue = _d.defaultValue, description = _d.description, descriptionProps = _d.descriptionProps, _l = _d.disabled, disabledProp = _l === void 0 ? false : _l, endAdornmentProp = _d.endAdornment, _m = _d.fullWidth, fullWidth = _m === void 0 ? false : _m, defaultId = _d.id, inputComponentProp = _d.inputComponent, inputProps = _d.inputProps, inputRefProp = _d.inputRef, label = _d.label, labelPropsProp = _d.labelProps, layout = _d.layout, _o = _d.multiline, multiline = _o === void 0 ? false : _o, name = _d.name, onBlur = _d.onBlur, onChangeProp = _d.onChange, onFocus = _d.onFocus, onKeyDown = _d.onKeyDown, onKeyUp = _d.onKeyUp, placeholder = _d.placeholder, _p = _d.plain, plain = _p === void 0 ? false : _p, readOnly = _d.readOnly, required = _d.required, requirementColor = _d.requirementColor, requirementType = _d.requirementType, _q = _d.rows, rows = _q === void 0 ? 1 : _q, rowsMax = _d.rowsMax, size = _d.size, startAdornmentProp = _d.startAdornment, styleProp = _d.style, _r = _d.styleReadOnlyAsDisabled, styleReadOnlyAsDisabled = _r === void 0 ? false : _r, tabIndex = _d.tabIndex, _s = _d.type, type = _s === void 0 ? 'text' : _s, validationMessage = _d.validationMessage, validationMessageProps = _d.validationMessageProps, validationStatus = _d.validationStatus, value = _d.value, restProps = __rest(_d, ["active", 'aria-label', "autoComplete", "autoFocus", "autoEndAdornment", "autoStartAdornment", "borderless", "classes", "className", "cols", "compact", "component", "containerClasses", "containerProps", "defaultValue", "description", "descriptionProps", "disabled", "endAdornment", "fullWidth", "id", "inputComponent", "inputProps", "inputRef", "label", "labelProps", "layout", "multiline", "name", "onBlur", "onChange", "onFocus", "onKeyDown", "onKeyUp", "placeholder", "plain", "readOnly", "required", "requirementColor", "requirementType", "rows", "rowsMax", "size", "startAdornment", "style", "styleReadOnlyAsDisabled", "tabIndex", "type", "validationMessage", "validationMessageProps", "validationStatus", "value"]);
    // Break up the input props to preserve the `className` prop
    var _t = inputProps || {}, inputPropsClassName = _t.className, inputPropsRest = __rest(_t, ["className"]);
    var id = useId(displayName, { defaultId: defaultId });
    var labelProps = React.useMemo(function () { return (__assign({ htmlFor: id }, labelPropsProp)); }, [id, labelPropsProp]);
    // Cast `inputComponentProp` so we get type checking on its props
    // rather than having them be automatically cast to `any`.
    var InputComponent = inputComponentProp || (multiline ? 'textarea' : 'input');
    var hasError = validationStatus === 'error';
    var isValidated = !!validationStatus || hasError;
    var isValidatedSuccess = validationStatus === 'success';
    var isValidatedWarning = validationStatus === 'warning';
    // Read from the form group context, if it is inside a <FormGroup/>
    var groupContext = React.useContext(FormGroupContext);
    var disabled = (groupContext === null || groupContext === void 0 ? void 0 : groupContext.disabled) || disabledProp;
    var shouldStyleAsDisabled = disabled || (!!readOnly && styleReadOnlyAsDisabled);
    // Refs
    var internalInputRef = React.useRef(null);
    var inputRef = useCombineRefs([inputRefProp, internalInputRef]);
    var width = useDynamicLayoutCalculation({ ref: internalInputRef }).width;
    var onChange = React.useCallback(function (ev) {
        if (multiline && internalInputRef.current) {
            setMultiLineInputHeight({ el: internalInputRef.current, rows: rows, rowsMax: rowsMax });
        }
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(ev);
    }, [multiline, onChangeProp, rows, rowsMax]);
    // Recalculate the input element's height whenever its width or relevant variables change.
    useIsomorphicLayoutEffect(function () {
        // Check for the `style` property in case the user has customized the input element
        // and is not passing the ref as expected.
        if (!internalInputRef.current || !internalInputRef.current.style)
            return;
        if (multiline) {
            setMultiLineInputHeight({ el: internalInputRef.current, rows: rows, rowsMax: rowsMax });
        }
        else {
            // Reset the height of the <input> in case we've switched between multi-line and single-line.
            internalInputRef.current.style.height = '100%';
        }
    }, [multiline, rows, rowsMax, width]);
    // Styles
    var baseClasses = useInputStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var theme = useTheme();
    var disabledIconColor = theme.hawkins.icon.fillXlight;
    var errorIconColor = theme.hawkins.icon.fillInformationError;
    var regularIconColor = theme.hawkins.icon.fillRegular;
    var successIconColor = theme.hawkins.icon.fillInformationSuccess;
    var warningIconColor = theme.hawkins.icon.fillInformationWarning;
    var endAdornmentOverrideProps = React.useMemo(function () {
        var color = regularIconColor;
        return {
            Icon: { size: iconSize, style: { color: color } },
            ThirdPartyIcon: { size: iconSize, style: { color: color } },
        };
    }, [regularIconColor]);
    var startAdornmentOverrideProps = React.useMemo(function () {
        var color = shouldStyleAsDisabled ? disabledIconColor : regularIconColor;
        if (hasError) {
            color = errorIconColor;
        }
        else if (isValidatedSuccess) {
            color = successIconColor;
        }
        else if (isValidatedWarning) {
            color = warningIconColor;
        }
        return {
            Icon: { size: iconSize, style: { color: color } },
            ThirdPartyIcon: { size: iconSize, style: { color: color } },
        };
    }, [
        disabledIconColor,
        errorIconColor,
        hasError,
        isValidatedSuccess,
        isValidatedWarning,
        regularIconColor,
        shouldStyleAsDisabled,
        successIconColor,
        warningIconColor,
    ]);
    var endAdornment = endAdornmentProp;
    var startAdornment = startAdornmentProp;
    if (autoEndAdornment) {
        if (shouldStyleAsDisabled) {
            endAdornment = _jsx(IconLock, { className: classes.disabledIcon });
        }
        else if (hasError) {
            endAdornment = _jsx(IconAlert, { style: { color: errorIconColor } });
        }
        else if (isValidatedWarning) {
            endAdornment = _jsx(IconWarning, { style: { color: warningIconColor } });
        }
        else if (isValidatedSuccess) {
            endAdornment = _jsx(IconSuccess, { style: { color: successIconColor } });
        }
    }
    if (autoStartAdornment) {
        if (type === 'search')
            startAdornment = _jsx(IconSearch, {});
    }
    var adornmentContainerBaseClassName = clsx(classes.adornmentContainer, (_a = {},
        _a[classes.adornmentContainerMultiline] = multiline,
        _a[classes.adornmentContainerMultilineCompact] = multiline && compact,
        _a));
    var endAdornmentContainerClassName = clsx(adornmentContainerBaseClassName, classes.endAdornmentContainer);
    var inputClassName = clsx(classes.input, (_b = {},
        _b[classes.inputCompact] = compact,
        _b[classes.inputDisabled] = shouldStyleAsDisabled,
        _b[classes.inputMultiline] = multiline,
        _b));
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_c = {},
        _c[classes.rootActive] = active,
        _c[classes.rootBorderless] = borderless,
        _c[classes.rootCompact] = compact,
        _c[classes.rootDisabled] = shouldStyleAsDisabled,
        _c[classes.rootMultiline] = multiline,
        _c[classes.rootPlain] = plain,
        _c[classes.rootValidated] = isValidated,
        _c[classes.rootValidatedError] = hasError,
        _c[classes.rootValidatedSuccess] = isValidatedSuccess,
        _c[classes.rootValidatedWarning] = isValidatedWarning,
        _c));
    var startAdornmentContainerClassName = clsx(adornmentContainerBaseClassName, classes.startAdornmentContainer);
    return (_jsx(FormFieldContainer, __assign({ classes: containerClasses, compact: compact, description: description, descriptionProps: descriptionProps, fullWidth: fullWidth, label: label, labelProps: labelProps, requirementColor: requirementColor, requirementType: requirementType, validationMessage: validationMessage, validationMessageProps: validationMessageProps, validationStatus: validationStatus }, containerProps, { children: _jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [!isNil(startAdornment) && (_jsx("div", __assign({ className: startAdornmentContainerClassName }, { children: _jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: startAdornmentOverrideProps }, { children: startAdornment })) }))), _jsx(InputComponent, __assign({ "aria-label": ariaLabel, autoComplete: autoComplete, autoFocus: autoFocus, className: clsx(inputClassName, inputPropsClassName), cols: cols, defaultValue: defaultValue, disabled: disabled, id: id, name: name, onBlur: onBlur, onChange: onChange, onFocus: onFocus, onKeyDown: onKeyDown, onKeyUp: onKeyUp, placeholder: placeholder, readOnly: readOnly, ref: inputRef, required: required, rows: multiline ? Number(rows) || 1 : undefined, size: size, tabIndex: tabIndex, type: type, value: value }, inputPropsRest)), _jsx("div", { className: classes.validationBorderAnchor }), _jsx("div", { 
                    // HACK: <input> elements can't have an `::after` pseudo-element,
                    // which is how the Hawkins focus ring is implemented,
                    // so we need an extra element to act as the focus ring anchor.
                    className: classes.focusRingAnchor }), !isNil(endAdornment) && (_jsx("div", __assign({ className: endAdornmentContainerClassName }, { children: _jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: endAdornmentOverrideProps }, { children: endAdornment })) })))] })) })));
});
Input.displayName = displayName;
